import React, { useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import Select from 'react-select';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import format from 'date-fns/format';
import { useParams } from 'react-router';
import { Checkbox } from '@mui/material';
import usePermissions from '../hooks/usePermissions';
import useGetUserRole from '../hooks/useGetUserRole';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import tinyMCEConfigs from '../utils/tinymce';

const schema = yup
  .object({
    assignmentTitle: yup.string().required('assignment title is required'),
    deadline: yup
      .date()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null ? null : value
      ),
    maxAttempts: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null ? null : value
      ),
    grader: yup.string().nullable(),
    inputType: yup.string().required('input type is required'),
    isCapstone: yup.boolean().nullable(),
  })
  .required();

function EditAssignment({
  toggleModal,
  assignmentContent,
  instructions,
  assignmentTitle: topic,
  pseudocodeSolution,
  deadline,
  maxAttempts,
  grader,
  inputType,
  isCapstone,
}) {
  const { assignmentId } = useParams();
  const [bodyContentIsDirty, setBodyContentIsDirty] = useState(false);
  const [instructionContentIsDirty, setInstructionsContentIsDirty] =
    useState(false);
  const [pseudocodeSolutionIsDirty, setPseudocodeSolutionIsDirty] =
    useState(false);
  const [assignmentBody, setAssignmentBody] = useState(assignmentContent);
  const [assignmentInstructions, setAssignmentInstructions] =
    useState(instructions);
  const [assignmentPseudocodeSolution, setAssignmentPseudocodeSolution] =
    useState(pseudocodeSolution);

  const permissions = usePermissions();
  const role = useGetUserRole();

  const canUpdateAssignment =
    permissions?.includes('UpdateAssignment') || role === 'SuperAdmin';

  const graderOptions = [
    {
      label: 'AI',
      value: 'ai',
    },
    {
      label: 'Manual',
      value: 'manual',
    },
  ];

  const inputTypeOptions = [
    {
      label: 'Github Link',
      value: 'github',
    },
    {
      label: 'Google Docs Link',
      value: 'google_docs',
    },
    {
      label: 'LLM Chat',
      value: 'llm_chat',
    },
    {
      label: 'Any',
      value: 'any_link',
    },
  ];

  // check if is empty

  const isBodyEmpty = assignmentBody === '<p><br></p>' || assignmentBody === '';

  const isInstructionsEmpty =
    assignmentInstructions === '<p><br></p>' || assignmentInstructions === '';

  const isPseudocodeSolutionEmpty =
    assignmentPseudocodeSolution === '<p><br></p>' ||
    assignmentPseudocodeSolution === '';

  const instructionEditorRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const pseudocodeSolutionRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      deadline: deadline
        ? format(new Date(deadline), "yyyy-MM-dd'T'HH:mm")
        : null,
      maxAttempts: maxAttempts || null,
      grader: grader || null,
      inputType: inputType || null,
      assignmentTitle: topic || null,
      isCapstone: isCapstone || null,
    },
  });

  const selectedGrader = watch('grader');
  const queryClient = useQueryClient();

  const saveBodyContent = () => {
    if (bodyEditorRef.current) {
      const content = bodyEditorRef.current.getContent();
      setBodyContentIsDirty(false);
      bodyEditorRef.current.setDirty(false);
      setAssignmentBody(content);
    }
  };

  const saveInstructionsContent = () => {
    if (instructionEditorRef.current) {
      const content = instructionEditorRef.current.getContent();
      setInstructionsContentIsDirty(false);
      instructionEditorRef.current.setDirty(false);
      setAssignmentInstructions(content);
    }
  };

  const savePseudocodeSolutionContent = () => {
    if (pseudocodeSolutionRef.current) {
      const content = pseudocodeSolutionRef.current.getContent();
      setPseudocodeSolutionIsDirty(false);
      pseudocodeSolutionRef.current.setDirty(false);
      setAssignmentPseudocodeSolution(content);
    }
  };

  const onEditAssignmentSuccess = () => {
    queryClient.invalidateQueries(['assignment', assignmentId]);
    toast.success('Assignment updated successfully.');

    toggleModal();
  };

  const onEditAssignmentFailure = () => {
    toast.error("Couldn't update assignment. Please try again!");
  };

  const { mutate, isLoading } = useMutation(
    (data) => {
      return axiosInstancev2.put(`/assignments/${assignmentId}`, data);
    },
    { onSuccess: onEditAssignmentSuccess, onError: onEditAssignmentFailure }
  );
  const editAssignment = async (data) => {
    const requestBody = {
      title: data?.assignmentTitle,
      body: assignmentBody,
      instructions: assignmentInstructions,
      pseudocodeSolution: assignmentPseudocodeSolution,
      deadline: data?.deadline || undefined,
      max_attempts: data?.maxAttempts || undefined,
      grader: data?.grader || undefined,
      input_type: data?.inputType || undefined,
      is_capstone: data?.isCapstone || undefined,
    };

    mutate(requestBody);
  };

  const buttonDisabled =
    instructionContentIsDirty ||
    bodyContentIsDirty ||
    pseudocodeSolutionIsDirty ||
    isBodyEmpty ||
    isInstructionsEmpty ||
    isPseudocodeSolutionEmpty ||
    Object.keys(errors).length > 0;

  return (
    <form className="space-y-4" onSubmit={handleSubmit(editAssignment)}>
      <div className="flex flex-col space-y-2 w-full">
        <label htmlFor="assignmentTitle" className="text-base font-semibold">
          Assignment Title
        </label>
        <input
          {...register('assignmentTitle', { required: true })}
          type="text"
          id="assignmentTitle"
          placeholder="Assignment Title"
          className="border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none"
        />
        {errors.assignmentTitle && (
          <span className="text-red-600 text-xs mt-2">
            {errors.assignmentTitle.message}
          </span>
        )}
      </div>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col space-y-1">
          <label htmlFor="deadline" className="text-base font-semibold">
            Deadline (Optional)
          </label>
          <input
            {...register('deadline')}
            type="datetime-local"
            min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
            id="deadline"
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.deadline ? 'border-red-600' : ''
            }`}
          />
          {errors.deadline && (
            <span className="text-red-600 text-xs mt-2">
              {errors.deadline.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="maxAttempts" className="text-base font-semibold">
            Max Attempts (Optional)
          </label>
          <input
            {...register('maxAttempts')}
            type="number"
            id="maxAttempts"
            min={1}
            max={10}
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.maxAttempts ? 'border-red-600' : ''
            }`}
          />
          {errors.maxAttempts && (
            <span className="text-red-600 text-xs mt-2">
              {errors.maxAttempts.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="grader" className="text-base font-semibold">
            Grader
          </label>
          <Select
            placeholder="Select Grader"
            isClearable
            className="react-dropdown z-50"
            classNamePrefix="dropdown"
            options={graderOptions}
            value={graderOptions.filter(
              (option) => option.value === selectedGrader
            )}
            onChange={(event) => {
              setValue('grader', event?.value);
            }}
          />
          {errors.grader && (
            <span className="text-red-600  mt-2">{errors.grader?.message}</span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="inputType" className="text-base font-semibold">
            Input Type
          </label>
          <Select
            placeholder="Select Input Type"
            isClearable
            className="react-dropdown z-50"
            classNamePrefix="dropdown"
            options={inputTypeOptions}
            {...register('inputType')}
            onChange={(event) => {
              setValue('inputType', event?.value);
            }}
            value={inputTypeOptions.filter(
              (option) => option.value === watch('inputType')
            )}
          />
          {errors.inputType && (
            <span className="text-red-600  mt-2">
              {errors.inputType?.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <div className="flex items-center space-x-2">
            <label htmlFor="isCapstone" className="text-base font-semibold">
              Is Capstone
            </label>
            <Checkbox
              {...register('isCapstone')}
              checked={watch('isCapstone') || false}
              onChange={(e) => setValue('isCapstone', e.target.checked)}
            />
          </div>
          {errors.isCapstone && (
            <span className="text-red-600  mt-2">
              {errors.isCapstone?.message}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col space-y-2">
        <label
          htmlFor="assignmentInstructions"
          className="text-base font-semibold"
        >
          Assignment Instructions
        </label>
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          initialValue={assignmentInstructions}
          onInit={(evt, editor) => {
            instructionEditorRef.current = editor;
          }}
          onDirty={() => setInstructionsContentIsDirty(true)}
          init={{
            ...tinyMCEConfigs,
            editable_root: canUpdateAssignment,
            save_onsavecallback: saveInstructionsContent,
          }}
        />
        {instructionContentIsDirty && (
          <p>You have unsaved assignment instructions content!</p>
        )}
      </div>
      <div className="flex flex-col space-y-2">
        <label htmlFor="assignmentContent" className="text-base font-semibold">
          Assignment Body
        </label>
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          initialValue={assignmentBody}
          onInit={(evt, editor) => {
            bodyEditorRef.current = editor;
          }}
          onDirty={() => setBodyContentIsDirty(true)}
          init={{
            ...tinyMCEConfigs,
            editable_root: canUpdateAssignment,
            save_onsavecallback: saveBodyContent,
          }}
        />
        {bodyContentIsDirty && <p>You have unsaved assignment body content!</p>}
      </div>
      <div className="flex flex-col space-y-2">
        <label
          htmlFor="assignmentPseudocodeSolution"
          className="text-base font-semibold"
        >
          Step by Step Solution
        </label>
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          initialValue={assignmentPseudocodeSolution}
          onInit={(evt, editor) => {
            pseudocodeSolutionRef.current = editor;
          }}
          onDirty={() => setPseudocodeSolutionIsDirty(true)}
          init={{
            ...tinyMCEConfigs,
            editable_root: canUpdateAssignment,
            save_onsavecallback: savePseudocodeSolutionContent,
          }}
        />
        {pseudocodeSolutionIsDirty && (
          <p>You have unsaved step by step solution content!</p>
        )}
      </div>
      {isLoading ? (
        <div className="mt-5 grid items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className=" flex justify-between mt-8">
          <button
            className={`border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center `}
            type="button"
            onClick={toggleModal}
          >
            <CancelIcon fontSize="inherit" />
            <p>Cancel</p>
          </button>
          <button
            className={`px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center  ${
              buttonDisabled
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-claret-500'
            }`}
            type="submit"
            disabled={buttonDisabled}
          >
            <SaveIcon fontSize="inherit" />
            <p>Save Changes</p>
          </button>
        </div>
      )}
    </form>
  );
}

export default EditAssignment;
